import { GROUP_ACCESS_TIERS } from '@leanin/shared/views/event';
import type { AdminAccess } from './AdminAccessPolicyChoice';

// Which access tiers the Visibility tab offers, per kind and viewer. Pure so
// the product rules below are pinned by tests without rendering the section.

export type GroupVisibilityKind = 'circle' | 'network' | 'space';

export type AccessChoiceInput =
  | { readonly kind: 'circle'; readonly hasParentNetwork: boolean }
  | {
      readonly kind: 'network';
      readonly isPlatformAdmin: boolean;
      readonly current: AdminAccess;
    }
  | { readonly kind: 'space' };

export type AccessChoice = {
  /** Tiers hidden from the picker. */
  readonly omit: ReadonlyArray<AdminAccess>;
  /** The viewer sees the current tier but cannot change it. */
  readonly locked: boolean;
};

// Access options hidden per kind.
//
// Networks DO expose `unlisted`: a company Network wants to be reachable by
// the link its admins send out while staying out of the public directory, and
// that is exactly the tier. Hiding it here also made the value one-way — the
// platform-admin create flow offers all three, so a Network could be created
// `unlisted` and then never set back to it, with the Visibility tab rendering
// no card selected because the row's own value wasn't in the list.
//
// Circles still omit it: `network` covers the middle ground they actually
// need (visible to the parent Network, nobody else), and a Circle reachable
// by link-but-not-listed is the mode leaders read as "private" and get wrong.
//
// `network` is hidden for every kind here and added back for a Circle that
// actually has a parent Network: the tier scopes discovery TO that Network,
// so offering it without one would let a leader pick a setting that hides the
// Circle from everybody.
//
// `network_leaders` is hidden for every kind here and added back for platform
// admins editing a Network: only Networks can carry it, and only platform
// admins may set it or move a Network off it (the server rejects anyone else
// with FORBIDDEN).
const ACCESS_OMIT: Record<GroupVisibilityKind, ReadonlyArray<AdminAccess>> = {
  circle: ['unlisted', 'network', 'network_leaders'],
  network: ['network', 'network_leaders'],
  space: ['network', 'network_leaders'],
};

// A non-admin leader of a `network_leaders` Network sees that tier and
// nothing else, read-only — the server would reject any change they saved.
const NETWORK_LEADERS_ONLY_OMIT: ReadonlyArray<AdminAccess> =
  GROUP_ACCESS_TIERS.filter((a) => a !== 'network_leaders');

export function accessChoice(input: AccessChoiceInput): AccessChoice {
  switch (input.kind) {
    case 'circle':
      return {
        omit: input.hasParentNetwork
          ? ACCESS_OMIT.circle.filter((a) => a !== 'network')
          : ACCESS_OMIT.circle,
        locked: false,
      };
    case 'network':
      if (input.isPlatformAdmin) {
        return {
          omit: ACCESS_OMIT.network.filter((a) => a !== 'network_leaders'),
          locked: false,
        };
      }
      if (input.current === 'network_leaders') {
        return { omit: NETWORK_LEADERS_ONLY_OMIT, locked: true };
      }
      return { omit: ACCESS_OMIT.network, locked: false };
    case 'space':
      return { omit: ACCESS_OMIT.space, locked: false };
  }
}
